import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'

import { RouteError } from '@/components/route-error'
import { RoutePending } from '@/components/route-pending'
import { reportRangeSearchSchema } from '@/features/reports/components/report-range-search'
import { CustomerReportPage } from '@/features/reports/pages/customer-report'
import { requireCapability } from '@/lib/auth'

import type { CustomerIntelFilters, CustomerIntelPaging, ReportRangeSelection } from '@/types'

const PAGE_SIZE = 50

/* Reports → Customers. The range, the list being shown ("view") and its
   filters live in the URL so a filtered list ("at-risk medical patients") can
   be shared. The charts (second visits, cohorts, loyalty, queue) are in
   Analytics → Customers. */
const searchSchema = reportRangeSearchSchema.extend({
  view: z.enum(['all', 'contact', 'medical', 'birthdays', 'limit', 'discount', 'duplicates', 'wonback', 'slipping', 'spenddown']).optional(),
  segment: z.enum(['CHAMPION', 'REGULAR', 'NEW', 'AT_RISK', 'LAPSED', 'ONE_AND_DONE']).optional(),
  customerType: z.string().optional(),
  groupId: z.string().optional(),
  channel: z.enum(['ONLINE', 'IN_STORE', 'BOTH']).optional(),
  overdueOnly: z.boolean().optional(),
  contactableOnly: z.boolean().optional(),
  sort: z.enum(['value', 'overdue', 'spend', 'lastVisit', 'visits', 'profit']).optional(),
  page: z.number().int().min(1).optional(),
  limit: z.number().int().min(1).max(200).optional(),
})

export const Route = createFileRoute('/_dashboard/reports/customers')({
  beforeLoad: requireCapability('VIEW_REPORTS'),
  validateSearch: searchSchema,
  pendingComponent: RoutePending,
  errorComponent: RouteError,
  component: CustomersReportRoute,
})

function CustomersReportRoute() {
  const { view, segment, customerType, groupId, channel, overdueOnly, contactableOnly, sort, page, limit, ...range } = Route.useSearch()
  const navigate = Route.useNavigate()
  // Customer habits are read over months: default the range to the last 30 days.
  const selection: ReportRangeSelection = range.mode === 'preset' && !range.preset ? { ...range, preset: '30d' } : range
  const filters: CustomerIntelFilters = { view, segment, customerType, groupId, channel, overdueOnly, contactableOnly, sort }
  const paging: CustomerIntelPaging = { page: page ?? 1, limit: limit ?? PAGE_SIZE }
  return (
    <CustomerReportPage
      selection={selection}
      onSelectionChange={(next) => navigate({ search: (prev) => ({ ...prev, ...next, page: undefined }), replace: true })}
      filters={filters}
      onFiltersChange={(next) =>
        navigate({
          search: (prev) => ({ ...prev, ...next, view: next.view === 'all' ? undefined : next.view, overdueOnly: next.overdueOnly || undefined, contactableOnly: next.contactableOnly || undefined, page: undefined }),
          replace: true,
        })
      }
      paging={paging}
      onPagingChange={(next) => navigate({ search: (prev) => ({ ...prev, page: next.page > 1 ? next.page : undefined, limit: next.limit !== PAGE_SIZE ? next.limit : undefined }), replace: true })}
    />
  )
}
